import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useGetGuildEmojisQuery } from '../../features/guilds/guildsApi';
import { EmojiPicker, EmojiDisplay } from '../common/EmojiPicker';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import type { GuildRole, SelectedRole } from '../../types';

// Paid feature flag — flip to true to expose webhook sender branding fields in the UI.
// When false, the backend still applies sensible defaults (sender name = template name,
// avatar = hosted white square), so the user-visible result is just a better default,
// not the "Role Templates" placeholder.
const PAID_BRANDING_ENABLED = false;

interface TemplateFormData {
  name: string;
  messageContent: string;
  selectionMode: 'single' | 'multi';
  selectedRoles: SelectedRole[];
  webhookUsername?: string | null;
  webhookAvatarUrl?: string | null;
}

interface TemplateFormProps {
  guildId: string;
  roles: GuildRole[];
  onSubmit: (
    name: string,
    messageContent: string,
    selectedRoles: SelectedRole[],
    selectionMode: 'single' | 'multi',
    webhookUsername: string | null,
    webhookAvatarUrl: string | null
  ) => Promise<void>;
  onCancel: () => void;
  initialData?: TemplateFormData;
  isEditMode?: boolean;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({
  guildId,
  roles,
  onSubmit,
  onCancel,
  initialData,
  isEditMode = false,
}) => {
  const [templateName, setTemplateName] = useState(initialData?.name || '');
  const [messageContent, setMessageContent] = useState(initialData?.messageContent || '');
  const [selectionMode, setSelectionMode] = useState<'single' | 'multi'>(initialData?.selectionMode || 'multi');
  const [selectedRoles, setSelectedRoles] = useState<SelectedRole[]>(initialData?.selectedRoles || []);
  const [webhookUsername, setWebhookUsername] = useState(initialData?.webhookUsername || '');
  const [webhookAvatarUrl, setWebhookAvatarUrl] = useState(initialData?.webhookAvatarUrl || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setTemplateName(initialData.name);
      setMessageContent(initialData.messageContent);
      setSelectionMode(initialData.selectionMode || 'multi');
      setSelectedRoles(initialData.selectedRoles);
      setWebhookUsername(initialData.webhookUsername || '');
      setWebhookAvatarUrl(initialData.webhookAvatarUrl || '');
    }
  }, [initialData]);

  // Fetch guild emojis
  const { data: guildEmojis = [] } = useGetGuildEmojisQuery(guildId);

  const manageableRoles = roles
    .filter((role) => role.manageable)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleRoleToggle = (role: GuildRole) => {
    setSelectedRoles((prev) => {
      const exists = prev.find((r) => r.roleId === role.id);
      if (exists) {
        return prev.filter((r) => r.roleId !== role.id);
      } else {
        return [
          ...prev,
          {
            roleId: role.id,
            name: role.name,
            emoji: '🎭',
            color: role.color,
          },
        ];
      }
    });
  };

  const handleEmojiChange = (roleId: string, emoji: string) => {
    setSelectedRoles((prev) =>
      prev.map((role) => (role.roleId === roleId ? { ...role, emoji } : role))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!templateName.trim() || !messageContent.trim() || selectedRoles.length === 0) {
      toast.error('Please fill in all fields and select at least one role.');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(
        templateName,
        messageContent,
        selectedRoles,
        selectionMode,
        webhookUsername.trim() || null,
        webhookAvatarUrl.trim() || null
      );
      // Reset form
      setTemplateName('');
      setMessageContent('');
      setSelectionMode('multi');
      setSelectedRoles([]);
      setWebhookUsername('');
      setWebhookAvatarUrl('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentTimestamp = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-12 gap-8">
        {/* Form Panel */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-surface-container rounded-lg p-8 shadow-2xl relative overflow-hidden">
            {/* Left accent ribbon */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />

            {/* Title */}
            <h3 className="text-2xl font-extrabold tracking-tight text-on-surface mb-8">
              {isEditMode ? 'Edit Template' : 'Create New Template'}
            </h3>

            {/* Template Name */}
            <div className="mb-6">
              <label
                htmlFor="templateName"
                className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2"
              >
                Template Name
              </label>
              <Input
                id="templateName"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Welcome Roles"
                disabled={isSubmitting}
              />
            </div>

            {/* Message Content */}
            <div className="mb-6">
              <label
                htmlFor="messageContent"
                className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2"
              >
                Message Content
              </label>
              <Textarea
                id="messageContent"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Enter the message to display with the role buttons..."
                rows={4}
                disabled={isSubmitting}
              />
            </div>

            {/* Sender Branding — gated paid feature.
                When PAID_BRANDING_ENABLED is false this entire block is hidden and
                the backend uses defaults (sender name = template.name, avatar =
                DEFAULT_AVATAR_URL). Flip the constant at the top of this file to
                expose the controls. */}
            {PAID_BRANDING_ENABLED && (
              <div className="mb-6 rounded-lg ring-1 ring-outline-variant/20 p-4 bg-surface-container-low">
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-base text-primary-text">workspace_premium</span>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-on-surface">
                    Sender Branding
                  </h4>
                </div>
                <p className="text-xs text-on-surface-variant mb-4">
                  Override how this template's messages appear in Discord. Leave blank to
                  use the template name and a neutral avatar.{' '}
                  <span className="text-warning">Branding changes only apply to messages sent after saving — Discord won't update existing live messages.</span>
                </p>

                <div className="mb-4">
                  <label
                    htmlFor="webhookUsername"
                    className="block text-[11px] font-bold uppercase tracking-widest text-on-surface-variant mb-2"
                  >
                    Sender Name
                  </label>
                  <Input
                    id="webhookUsername"
                    type="text"
                    value={webhookUsername}
                    onChange={(e) => setWebhookUsername(e.target.value)}
                    placeholder={templateName || 'e.g., Welcome Bot'}
                    maxLength={80}
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label
                    htmlFor="webhookAvatarUrl"
                    className="block text-[11px] font-bold uppercase tracking-widest text-on-surface-variant mb-2"
                  >
                    Sender Avatar URL
                  </label>
                  <Input
                    id="webhookAvatarUrl"
                    type="url"
                    value={webhookAvatarUrl}
                    onChange={(e) => setWebhookAvatarUrl(e.target.value)}
                    placeholder="https://example.com/avatar.png"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}

            {/* Selection Mode */}
            <div className="mb-6">
              <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-2">
                Selection Mode
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectionMode('multi')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectionMode === 'multi'
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-highest text-on-surface-variant ring-1 ring-outline-variant/30'
                  }`}
                  disabled={isSubmitting}
                >
                  Multi-select
                </button>
                <button
                  type="button"
                  onClick={() => setSelectionMode('single')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectionMode === 'single'
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-highest text-on-surface-variant ring-1 ring-outline-variant/30'
                  }`}
                  disabled={isSubmitting}
                >
                  Single-select
                </button>
              </div>
              <p className="text-on-surface-variant/60 text-xs mt-2">
                {selectionMode === 'single'
                  ? 'Users can pick one role at a time. Selecting a new role removes the previous one.'
                  : 'Users can pick multiple roles from the list.'}
              </p>
              <div className="flex items-start gap-2 mt-3 p-3 bg-warning/10 rounded-lg ring-1 ring-warning/20">
                <span className="material-symbols-outlined text-warning text-base mt-0.5 shrink-0">info</span>
                <p className="text-on-surface-variant text-xs leading-relaxed">
                  Do not use the same role in both single-select and multi-select templates. Single-select templates remove all other template roles when a user picks one, which can interfere with roles assigned by multi-select templates.
                </p>
              </div>
            </div>

            {/* Role Selection */}
            <div className="mb-8">
              <label className="block text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-3">
                Select Roles
              </label>

              {manageableRoles.length === 0 ? (
                <div className="flex items-center gap-3 p-4 bg-surface-container-low rounded-lg ring-1 ring-outline-variant/10">
                  <span className="material-symbols-outlined text-warning text-xl">warning</span>
                  <p className="text-on-surface-variant text-sm">
                    No manageable roles available. The bot needs a role higher than the roles you want to manage.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 p-4 bg-surface-container-low rounded-lg ring-1 ring-outline-variant/10">
                    {manageableRoles.map((role) => {
                      const selected = selectedRoles.find((r) => r.roleId === role.id);
                      const hexColor = role.color
                        ? `#${role.color.toString(16).padStart(6, '0')}`
                        : '#b5bac1';

                      return (
                        <div
                          key={role.id}
                          className="flex items-center gap-2"
                        >
                          <button
                            type="button"
                            onClick={() => handleRoleToggle(role)}
                            disabled={isSubmitting}
                            className={`flex items-center gap-2 px-3 py-1.5 bg-surface-container-highest rounded-full cursor-pointer hover:bg-surface-bright transition-all ${
                              selected
                                ? 'ring-2 ring-primary'
                                : 'ring-1 ring-outline-variant/30'
                            }`}
                          >
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: hexColor }}
                            />
                            <span className="text-xs font-medium text-on-surface">
                              {role.name}
                            </span>
                          </button>
                          {selected && (
                            <EmojiPicker
                              value={selected.emoji}
                              onChange={(emoji) => handleEmojiChange(role.id, emoji)}
                              guildEmojis={guildEmojis}
                              disabled={isSubmitting}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-on-surface-variant/60 text-xs mt-2">
                    {selectedRoles.length} role{selectedRoles.length !== 1 ? 's' : ''} selected. Click badges to toggle. Use the emoji picker to change icons.
                  </p>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-6 border-t border-outline-variant/10">
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">
                      {isEditMode ? 'save' : 'add_circle'}
                    </span>
                    {isEditMode ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-surface-container-low rounded-lg p-6 space-y-4 lg:sticky lg:top-6">
            {/* Preview Header */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-on-surface-variant">
                Live Preview
              </span>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              </span>
            </div>

            {/* Discord Message Preview */}
            <div className="bg-surface-container rounded-lg p-4 space-y-3">
              {/* Bot header */}
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <span
                    className="material-symbols-outlined text-surface text-lg"
                    style={{ fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}
                  >
                    architecture
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold text-on-surface">System Architect</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/20 text-primary-text px-1.5 py-0.5 rounded">
                    BOT
                  </span>
                  <span className="text-[10px] text-on-surface-variant/50">
                    {currentTimestamp}
                  </span>
                </div>
              </div>

              {/* Message content */}
              <p className="text-sm text-on-surface leading-relaxed pl-[52px]">
                {messageContent || 'Your message content will appear here...'}
              </p>

              {/* Role badges preview */}
              {selectedRoles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-[52px] pt-1">
                  {selectedRoles.map((role) => {
                    const hexColor = role.color
                      ? `#${role.color.toString(16).padStart(6, '0')}`
                      : '#b5bac1';
                    return (
                      <span
                        key={role.roleId}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                        style={{
                          backgroundColor: `${hexColor}22`,
                          color: hexColor,
                          borderColor: `${hexColor}44`,
                        }}
                      >
                        <EmojiDisplay emoji={role.emoji} className="w-3 h-3" /> {role.name}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </form>
  );
};
